import { REFLECTION_TYPE, WAVE_STYLE } from '../../PreviewDisplayConstants';

// The visualizer's box at rest: the shape it has while nothing is playing, in base units, with its
// middle measured from the visualizer's own position.
//
// The drag outline is taken from this rather than from what is drawn. The waves grow out of the rest
// shape with the music and bounce scales the whole of it, so a box read off the display tree pulsed to
// the beat under the pointer and changed shape with every wave. The rest shape changes only when a
// setting does: the Radius or Width, a layer's scale, the logo.
//
// Worked out from the settings alone, by following the drawing code in WaveCircle and FlatWave with a
// silent spectrum. A change to where either of them draws its rest shape has to be carried over here.

// The circle's ring is radiusFactor units out at a layer scale of 1, and its logo is a square
// logoSizeFactor of the ring across. Both are centred on the position, and a round shape gives the same
// box however the visualizer is rotated or spun.
export function measureWaveCircleRestBox(config) {
    const layers = findVisibleLayers(config);

    if (layers.length === 0 && !config.logoVisible)
        return null;

    const radiusFactor = config.radiusFactor ?? 0;
    const ringRadii = layers.map(layer => radiusFactor * (layer.scale ?? 1));
    const logoRadius = config.logoVisible ? radiusFactor * (config.logoSizeFactor ?? 0) : 0;
    const radius = Math.max(logoRadius, ...ringRadii);

    return { centerX: 0, centerY: 0, halfWidth: radius, halfHeight: radius };
}

// The box around every visible layer of the flat wave, once each layer is scaled and turned by its own
// settings and the whole wave is turned by its Rotation.
export function measureFlatWaveRestBox(config) {
    const layers = findVisibleLayers(config);

    if (layers.length === 0)
        return null;

    const visualizerRotation = degreesToRadians(config.rotation ?? 0);
    const corners = layers
        .flatMap(layer => measureFlatLayerRestCorners(config, layer))
        .map(corner => rotatePoint(corner, visualizerRotation));

    return measureBoxAroundPoints(corners);
}

// One flat layer at rest is a rectangle the full Width across, reaching baseHeight (plus the layer's
// own height adjustment) above the line through the position.
function measureFlatLayerRestCorners(config, layer) {
    const { waveStyle, reflection, rotation } = resolveFlatLayerOptions(config, layer);
    const halfWidth = (config.width ?? 0) * 0.5;
    const height = (config.baseHeight ?? 0) + (layer.heightAdjust ?? 0);
    const top = -height;
    const bottom = measureFlatLayerBottom(height, waveStyle, reflection);
    const scale = layer.scale ?? 1;

    return [
        { x: -halfWidth, y: top },
        { x: halfWidth, y: top },
        { x: halfWidth, y: bottom },
        { x: -halfWidth, y: bottom }
    ].map(corner => rotatePoint({ x: corner.x * scale, y: corner.y * scale }, rotation));
}

// Solid, Line and Bar close on the line through the position, and a reflection that mirrors the wave
// below that line takes the bottom as far down as the top goes up. Point is the exception: it draws dots
// along the top and nothing down to the line, so an unmirrored point layer is a single row.
function measureFlatLayerBottom(height, waveStyle, reflection) {
    if (reflection === REFLECTION_TYPE.TWO_SIDE || reflection === REFLECTION_TYPE.COMBO)
        return height;

    return waveStyle === WAVE_STYLE.POINT ? -height : 0;
}

// A layer with Customize switched on carries its own style, reflection and rotation, and the
// visualizer's apply otherwise: the same choice FlatWave's getVisualizationOptions and getLayerRotation
// make when they draw it.
function resolveFlatLayerOptions(config, layer) {
    const { customOptions } = layer;
    const useCustom = Boolean(customOptions?.enabled);

    return {
        waveStyle: useCustom ? customOptions.waveStyle : config.waveStyle,
        reflection: useCustom ? customOptions.reflectionType : config.reflection,
        rotation: useCustom ? degreesToRadians(customOptions.rotation ?? 0) : 0
    };
}

// A hidden layer draws nothing, which is how both renderers treat one.
function findVisibleLayers(config) {
    return (config.waveCircles || []).filter(layer => layer.visible);
}

function degreesToRadians(degrees) {
    return degrees * Math.PI / 180;
}

// Turned the way a PIXI rotation turns: clockwise on screen, where y points down.
function rotatePoint(point, angle) {
    if (angle === 0)
        return point;

    const cosine = Math.cos(angle);
    const sine = Math.sin(angle);

    return { x: point.x * cosine - point.y * sine, y: point.x * sine + point.y * cosine };
}

function measureBoxAroundPoints(points) {
    const horizontalCoordinates = points.map(point => point.x);
    const verticalCoordinates = points.map(point => point.y);
    const left = Math.min(...horizontalCoordinates);
    const right = Math.max(...horizontalCoordinates);
    const top = Math.min(...verticalCoordinates);
    const bottom = Math.max(...verticalCoordinates);

    return {
        centerX: (left + right) * 0.5,
        centerY: (top + bottom) * 0.5,
        halfWidth: (right - left) * 0.5,
        halfHeight: (bottom - top) * 0.5
    };
}
